import React from 'react';
import { useNavigate } from 'react-router';

import { CustomAlert } from 'components/AlertsCollection';
import { ConfirmationDialogLabels } from 'components/ConfirmationDialogBox';
import ActionButton from 'components/Flux/Personalized/MyStudies/ActionsCell/ActionButton';
import { RESEND_ERROR_MESSAGE } from 'components/Flux/Personalized/MyStudies/ActionsCell/useResendStudy';
import { useMyStudiesProduct } from 'components/Flux/Personalized/MyStudies/product';
import { StudyActionResult } from 'components/Flux/shared/useStudyActions';
import { myStudyActionErrorMessage } from 'helper/flux/myStudyActionErrors';
import { getStudyActionsState, StudyViewer, toStudyActionRef } from 'helper/flux/myStudyRow';
import handleAtuthorizationErrors from 'helper/tokenError';
import { ResponseError } from 'services/api/base';
import { MyStudy } from 'types/Flux/Personalized/myStudies';

import './index.scss';

export type StudyActionKind = 'resend' | StudyActionResult;

// D12: na tela nova os diálogos usam os títulos do design e "Voltar" para desistir. Os textos
// do corpo de Cancelar e Excluir continuam os atuais (D1).
const RESEND_LABELS: ConfirmationDialogLabels = {
  title: 'Reenviar estudo?', confirmLabel: 'Reenviar', dismissLabel: 'Voltar',
};
const DIALOG_LABELS: Record<StudyActionResult, ConfirmationDialogLabels> = {
  cancel: { title: 'Cancelar estudo?', confirmLabel: 'Cancelar estudo', dismissLabel: 'Voltar' },
  delete: { title: 'Excluir estudo?', confirmLabel: 'Excluir', dismissLabel: 'Voltar' },
};

// Dica enquanto a ação está em andamento (o botão fica aria-disabled e o leitor de tela a lê)
export const BUSY_HINTS = {
  resend: 'Reenviando…',
  download: 'Preparando o arquivo…',
  cancel: 'Cancelando…',
  delete: 'Excluindo…',
};
export const DELETED_MESSAGE = 'Estudo excluído.';
const ERROR_MESSAGES: Record<StudyActionResult, string> = {
  cancel: 'Não foi possível cancelar o estudo. Tente novamente em instantes.',
  delete: 'Não foi possível excluir o estudo. Tente novamente em instantes.',
};

export interface ActionsCellProps {
  study: MyStudy;
  viewer: StudyViewer;
  /** Ação concluída com sucesso, para o destaque da linha (#2004). */
  onActionSuccess?: (studyId: number, kind: StudyActionKind) => void;
  /** Resultado de cada ação, para a tela mostrar num aviso único. É o único canal de erro. */
  onFeedback: (feedback: CustomAlert) => void;
}

/**
 * Coluna fixa de ações da aba Meus Estudos (.dc.html:309-314): Reenviar, Baixar, Cancelar e
 * Excluir, sempre presentes. A habilitação e as dicas vêm de `getStudyActionsState` (#1991), e
 * as ações reusam os hooks da Row antiga, que recebem só os campos que leem.
 */
export default function ActionsCell({
  study, viewer, onActionSuccess, onFeedback,
}: ActionsCellProps) {
  const navigate = useNavigate();
  const actionRef = toStudyActionRef(study);
  const state = getStudyActionsState(study, viewer);
  // Sessão expirada faz logout e 403 de permissão vai para /forbidden, como no Baixar; o resto
  // vira mensagem na tela, sem texto técnico (myStudyActionErrorMessage)
  const errorMessage = (fallback: string) => (error: unknown) => (
    handleAtuthorizationErrors(error as ResponseError, navigate)
      ? null
      : myStudyActionErrorMessage(error, fallback)
  );

  const { useDownload, useRowActions, useResend } = useMyStudiesProduct();
  const { download, isLoading: downloading } = useDownload(actionRef);
  const {
    confirmationDialogBox, confirmCancel, confirmDelete, cancelling, deleting,
  } = useRowActions(actionRef, {
    dialogLabels: DIALOG_LABELS,
    onFeedback,
    errorMessage: (error, kind) => errorMessage(ERROR_MESSAGES[kind])(error),
    onSuccess: (kind) => {
      if (kind === 'delete') onFeedback({ title: '', message: DELETED_MESSAGE, variant: 'success' });
      onActionSuccess?.(study.id, kind);
    },
  });
  const { confirmationDialogBox: resendDialogBox, confirmResend, resending } = useResend(
    actionRef,
    {
      labels: RESEND_LABELS,
      onFeedback,
      onSuccess: () => onActionSuccess?.(study.id, 'resend'),
      errorMessage: errorMessage(RESEND_ERROR_MESSAGE),
    },
  );

  return (
    <div className="my-studies-actions">
      <ActionButton
        id={`reruns-study-${study.id}`}
        label="Reenviar"
        icon="send"
        state={state.resend}
        busy={resending}
        busyHint={BUSY_HINTS.resend}
        onClick={confirmResend}
      />
      <ActionButton
        id={`download-action-${study.id}`}
        label="Baixar"
        icon="download"
        state={state.download}
        busy={downloading}
        busyHint={BUSY_HINTS.download}
        onClick={download}
      />
      <ActionButton
        id={`cancel-study-${study.id}`}
        label="Cancelar"
        icon="close"
        state={state.cancel}
        busy={cancelling}
        busyHint={BUSY_HINTS.cancel}
        onClick={confirmCancel}
      />
      <ActionButton
        id={`delete-study-${study.id}`}
        label="Excluir"
        icon="trash"
        state={state.remove}
        busy={deleting}
        busyHint={BUSY_HINTS.delete}
        onClick={confirmDelete}
      />
      {resendDialogBox}
      {confirmationDialogBox}
    </div>
  );
}
